import FlowSubjectRow from "../features/calculator/components/FlowSubjectRow";
import FlowChoice from "../features/calculator/components/FlowChoice";
import FlowLevelSelect from "../features/calculator/components/FlowLevelSelect";
import useFlowDefaults from "../features/calculator/hooks/useFlowDefaults";
/* src/components/GroupFive.jsx */
import React, { useState, useEffect, useMemo } from "react";
import { useCalculatorStoredValue } from "../contexts/CalculatorPersistenceContext";
import { useTZ } from "../contexts/TimezoneContext";
import { useGradeActions } from "../contexts/GradesContext";
import { getEntryForSubject } from "../utils/timezone";
import { getBoundaryResolutionOrder } from "../utils/boundaryCatalog";
import useBoundaryCatalog from "../hooks/useBoundaryCatalog";
import { getBoundarySubjectMeta } from "../data/subjectCatalog";
import { getPointsAboveBoundary } from "../features/subjects/utils/subjectScoring";

const COMPONENTS = {
  "Analysis And Approaches_SL": [
    ["p1", "Paper 1", 80, "40%"],
    ["p2", "Paper 2", 80, "40%"],
    ["ia", "IA", 20, "20%"],
  ],
  "Analysis And Approaches_HL": [
    ["p1", "Paper 1", 110, "30%"],
    ["p2", "Paper 2", 110, "30%"],
    ["p3", "Paper 3", 55, "20%"],
    ["ia", "IA", 20, "20%"],
  ],
  "Applications And Interpretation_SL": [
    ["p1", "Paper 1", 80, "40%"],
    ["p2", "Paper 2", 80, "40%"],
    ["ia", "IA", 20, "20%"],
  ],
  "Applications And Interpretation_HL": [
    ["p1", "Paper 1", 110, "30%"],
    ["p2", "Paper 2", 110, "30%"],
    ["p3", "Paper 3", 55, "20%"],
    ["ia", "IA", 20, "20%"],
  ],
};

const SUBJECT_VARIANTS = {
  "Analysis And Approaches": [/analysis\s+and\s+approaches/i],
  "Applications And Interpretation": [/applications\s+and\s+interp(?:etation)?/i],
};

const toCanonicalSubject = (raw) => {
  if (!raw) return null;
  const normalized = raw.replace(/\s+/g, " ").trim();
  for (const [canonical, patterns] of Object.entries(SUBJECT_VARIANTS)) {
    if (patterns.some((p) => p.test(normalized))) return canonical;
  }
  return normalized;
};

const parseMathSubjectKey = (key) => {
  const match = key.match(/^(SL|HL)\s+Mathematics:?\s+(.+)$/i);
  if (!match) return null;
  const level = match[1].toUpperCase();
  const canonicalSubject = toCanonicalSubject(match[2]);
  if (!canonicalSubject) return null;
  if (!COMPONENTS[`${canonicalSubject}_${level}`]) return null;
  return { key, level, subject: canonicalSubject };
};

function GroupFive({ groupId = "5", boundary = "m24", headerExtras = null }) {
  const { tz } = useTZ();
  const { updateGrade } = useGradeActions();
  const { dataByBoundary, catalogKeys, isLoading } = useBoundaryCatalog(boundary, {
    includeAllBoundaries: true,
  });

  const storagePath = `groups.${groupId}.groupFive`;
  useFlowDefaults(storagePath, "groupFive", !isLoading);
  const [subject, setSubject] = useCalculatorStoredValue(`${storagePath}.subject`, "");
  const [level, setLevel] = useCalculatorStoredValue(`${storagePath}.level`, "");
  const [scores, setScores] = useCalculatorStoredValue(`${storagePath}.scores`, {});
  const [boundaryNotFound, setBoundaryNotFound] = useState(false);

  useEffect(() => { setBoundaryNotFound(false); }, [boundary]);

  const LEVELS = ["SL", "HL"];
  const parsedMathSubjects = catalogKeys
    .filter((key) => getBoundarySubjectMeta(key)?.groupId === "5")
    .map(parseMathSubjectKey)
    .filter(Boolean);

  const subjects = level
    ? Array.from(
        new Set(
          parsedMathSubjects.filter((item) => item.level === level).map((item) => item.subject)
        )
      ).sort((a, b) => a.localeCompare(b))
    : [];

  const subjectKeyCandidates =
    subject && level
      ? parsedMathSubjects.filter((item) => item.level === level && item.subject === subject).map((item) => item.key)
      : [];
  const allSubjectKeyCandidates = Array.from(
    new Set([
      ...subjectKeyCandidates,
      ...(subject && level
        ? [`${level} Mathematics: ${subject}`, `${level} Mathematics ${subject}`]
        : []),
    ])
  );

  const componentKey = subject && level ? `${subject}_${level}` : null;
  const scoreFields = useMemo(
    () => (componentKey ? (COMPONENTS[componentKey] || []) : []),
    [componentKey]
  );

  const hasCompleteScores =
    !!componentKey &&
    scoreFields.length > 0 &&
    scoreFields.every(([k]) => scores[k] !== "");

  const boundaryOrder = getBoundaryResolutionOrder(boundary);

  let entry = null;
  let resolvedBoundary = null;
  if (hasCompleteScores) {
    for (const cb of boundaryOrder) {
      const cd = dataByBoundary[cb];
      if (!cd) continue;
      let candidateEntry = null;
      for (const sk of allSubjectKeyCandidates) {
        const found = getEntryForSubject(cd.timezones, sk, tz).entry;
        if (found) { candidateEntry = found; break; }
      }
      if (candidateEntry) { entry = candidateEntry; resolvedBoundary = cb; break; }
    }
  }
  const entryAvailable = Boolean(entry);

  useEffect(() => {
    if (isLoading) return;
    if (!hasCompleteScores) {
      if (boundaryNotFound) setBoundaryNotFound(false);
      return;
    }
    if (!entryAvailable) {
      setBoundaryNotFound((prev) => (prev ? prev : true));
    } else if (boundaryNotFound) {
      setBoundaryNotFound(false);
    }
  }, [isLoading, hasCompleteScores, entryAvailable, boundaryNotFound]);

  let grade = null;
  let neededText = null;
  let pointsAboveBoundary = null;
  let error = null;

  if (hasCompleteScores && entry) {
    if (!scoreFields.length) {
      error = `No components found for Mathematics: ${subject} ${level}`;
    } else {
      const total = scoreFields.reduce((sum, [k, , max, wt]) => {
        return sum + (Number(scores[k]) / max) * (parseFloat(wt) / 100) * 100;
      }, 0);
      const cutoffs = Array.from({ length: 7 }, (_, i) => parseFloat(entry[String(i + 1)]));
      if (cutoffs.some(isNaN)) {
        error = `Invalid grade boundaries for Mathematics: ${subject} ${level}`;
      } else {
        for (let i = 6; i >= 0; i--) {
          if (total >= cutoffs[i]) {
            grade = i + 1;
            if (i < 6) {
              neededText = `Need ${(cutoffs[i + 1] - total).toFixed(1)} percentage points for grade ${i + 2}`;
            }
            break;
          }
        }

        if (grade !== null) {
          pointsAboveBoundary = getPointsAboveBoundary(total, entry, grade);
        }
      }
    }
  }

  useEffect(() => {
    if (!subject || !level) return;

    setScores((currentScores) => {
      const nextScores = scoreFields.reduce(
        (acc, [k]) => ({ ...acc, [k]: currentScores[k] ?? "" }),
        {}
      );
      const keys = Object.keys(nextScores);
      const currentKeys = Object.keys(currentScores);
      const matchesCurrentScores =
        keys.length === currentKeys.length &&
        keys.every((key) => currentScores[key] === nextScores[key]);

      return matchesCurrentScores ? currentScores : nextScores;
    });
  }, [subject, level, scoreFields, setScores]);

  useEffect(() => {
    if (isLoading) return;

    if (grade !== null && subject && level) {
      updateGrade(groupId, grade, subject, level, neededText);
    } else {
      updateGrade(groupId, null, null, null);
    }
  }, [isLoading, grade, subject, level, neededText, updateGrade, groupId]);

  const fallbackBoundaryNote =
    resolvedBoundary && resolvedBoundary !== boundary
      ? `Result based on boundary "${resolvedBoundary}" because "${boundary}" is unavailable for this subject.`
      : null;

  if (isLoading) {
    return <div className="calculator-pad"><p>Loading…</p></div>;
  }
  if (Object.keys(dataByBoundary).length === 0) {
    return <div className="calculator-pad"><p>Boundary data unavailable.</p></div>;
  }

  return (
    <FlowSubjectRow groupId={groupId} fields={scoreFields} scores={scores} onScoresChange={setScores} grade={grade} neededText={neededText} pointsAboveBoundary={pointsAboveBoundary} fallbackBoundaryNote={fallbackBoundaryNote} error={error || (boundaryNotFound ? "Boundary not available. Try another boundary (e.g., m24)." : null)} levelControl={<FlowLevelSelect groupId={groupId} value={level} options={LEVELS} onChange={value => { setLevel(value); setScores({}); }} />} choices={<>
      {headerExtras}
      <FlowChoice label="Subject"><select value={subject} onChange={e => { setSubject(e.target.value); setScores({}); }}><option value="" disabled>Subject</option>{subjects.map(value => <option key={value} value={value}>{value.replace(/ And /g, " and ")}</option>)}</select></FlowChoice>
    </>} />
  );
}

export default React.memo(GroupFive);
